import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FaBell,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaCopy,
  FaFile,
  FaMapMarkerAlt,
  FaVideo,
} from "react-icons/fa";
import { FaAndroid, FaChartPie, FaCode } from "react-icons/fa";
import { HiX } from "react-icons/hi";
import { useNavigate } from "react-router-dom";

import LanguageSwitcher from "../components/LanguageSwitcher";
import { courses } from "../constants/common";
import { useAuth } from "../context/AuthContext";
import { useReminders } from "../context/RemindersContext";
import i18n from "../i18n";
import { formatCustomDate } from "../utils/formatCustomDate";
import { getCourseLabel } from "../utils/getCourseLabel";

// ---------------- Section Header ----------------
const SectionHeader = ({ title, actionLabel = "SeeAll", onAction, i18n, t }) => (
  <div className="mb-3 flex items-center justify-between">
    <h2 className="text-lg font-bold text-gray-700 sm:text-xl">{t(title)}</h2>
    {actionLabel && (
      <button
        onClick={onAction}
        className="flex items-center gap-1 rounded-full px-3 py-1 text-sm font-medium text-purple-600 transition hover:bg-purple-50 hover:text-purple-800 sm:text-sm"
      >
        {t(actionLabel)} {i18n.language === "ar" ? <FaChevronLeft /> : <FaChevronRight size={12} />}
      </button>
    )}
  </div>
);

// ---------------- Course Card ----------------
const CourseCard = React.memo(
  ({
    title,
    subtitle,
    t,
    icon,
    link,
    description,
    warningMessage,
    imageSrc,
    localLink,
    isStarted,
    isCompleted,
    isSurveyCompleted,
    onOpen,
  }) => {
    const handleClick = useCallback(() => {
      onOpen({
        title,
        description,
        warningMessage,
        link,
        imageSrc,
        localLink,
      });
    }, [title, description, warningMessage, link, imageSrc, localLink, onOpen]);

    // Shared card content (icon, title, description…)
    const CardContent = () => (
      <>
        <div className="flex items-center gap-4">
          <div
            className={`mb-2 flex h-12 w-12 items-center justify-center rounded-xl ${
              localLink ? "border border-indigo-100" : "border border-indigo-100"
            } text-indigo-600`}
          >
            {typeof icon === "string" ? (
              <img src={icon} alt={title} className="h-7 w-7 object-cover" />
            ) : (
              icon && <icon.type {...icon.props} className="text-2xl" />
            )}
          </div>
          {i18n.language === "ar" ? (
            <div>
              <h3 className="text-right text-sm font-semibold text-gray-800">
                {subtitle && t("fundamentals")} {t(title)}
              </h3>
            </div>
          ) : (
            <div>
              <h3 className="text-left text-sm font-semibold text-gray-800">
                {t(title)} {subtitle && t("fundamentals")}
              </h3>
            </div>
          )}
        </div>

        <p
          className={`text-sm leading-relaxed text-gray-600 ${
            i18n.language === "ar" ? "text-right" : "text-left"
          }`}
        >
          {t(description)}
        </p>

        {imageSrc && (
          <div className="mt-4 w-full overflow-hidden rounded-lg">
            <img src={imageSrc} alt={title} className="h-24 w-full object-cover" />
          </div>
        )}

        <div
          className={`mt-6 w-full rounded-lg bg-indigo-600 py-2 text-center text-sm font-medium text-white hover:bg-indigo-700 ${
            i18n.language === "ar" ? "font-bold" : ""
          }`}
        >
          {isCompleted && isSurveyCompleted
            ? t("courseCompleted")
            : isStarted
              ? t("resumeLearning")
              : t("startNow")}
        </div>
      </>
    );

    return (
      <button
        type="button"
        onClick={localLink ? localLink : handleClick}
        className="flex w-full flex-col justify-between space-y-6 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg"
      >
        <CardContent />
      </button>
    );
  }
);
// ---------------- Live Session ----------------
const LiveSessionCard = ({ session, i18n, t, handleClick, handleCopyPath, copySuccess }) => (
  <div className="flex flex-col gap-2 rounded-xl bg-gradient-to-br from-indigo-700 to-purple-800 p-4 shadow-md ring-1 ring-gray-200 transition hover:shadow-lg">
    {/* Badge + Button */}
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-2 rounded-full bg-red-200 px-3 py-0.5 text-sm font-bold text-red-600">
        <span className="h-2 w-2 animate-pulse rounded-full bg-red-500"></span>
        {t("LiveNow")}
      </span>
      <div className="flex items-center gap-2">
        <button
          onClick={() =>
            handleCopyPath(
              `${process.env.REACT_APP_BASE_URL}meeting?link=${encodeURIComponent(
                session.link
              )}&meetingID=${session.meeting_id}&sessionID=${session.id}&type=1`,
              session.id
            )
          }
          className="flex items-center gap-2 rounded-full border border-purple-500 bg-white/20 px-4 py-1.5 text-xs font-medium text-white/85 shadow-lg transition hover:bg-white/50 sm:px-5 sm:text-sm"
        >
          <FaCopy /> {copySuccess[session.id] ? t("Copied") : t("CopyLink")}
        </button>
        <button
          onClick={handleClick}
          className="flex items-center gap-2 rounded-full border border-purple-500 bg-white/20 px-4 py-1.5 text-xs font-medium text-white shadow-lg transition hover:bg-white/50 sm:px-5 sm:text-sm"
        >
          <FaVideo size={14} /> {t("JoinSession")}
        </button>
      </div>
    </div>

    {/* Content */}
    <div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex w-full flex-col overflow-hidden sm:flex-row sm:items-center sm:gap-3">
          <div className="flex items-center gap-2">
            <p className="line-clamp-1 flex-1 text-sm font-bold text-white sm:text-base">
              {getCourseLabel(session?.course_id, i18n.language)}
            </p>
          </div>
        </div>
      </div>
      <p className="mb-2 line-clamp-2 text-sm text-white/70">{session?.description}</p>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        {session?.coaches?.map((item, index) => (
          <span key={index} className="whitespace-nowrap text-xs italic text-purple-100 sm:text-sm">
            {index > 0 && <span className="mx-1 text-purple-400">/</span>}
            {item}
          </span>
        ))}
      </div>
    </div>
  </div>
);
// ---------------- Event Card (Styled like LiveSessionCard) ----------------
const LiveEventCard = ({ event, i18n, t, handleClick, handleCopyPath, copySuccess }) => {
  return (
    <div className="flex flex-col gap-2 rounded-xl bg-gradient-to-br from-indigo-700 to-purple-800 p-4 shadow-md ring-1 ring-gray-200 transition hover:shadow-lg">
      {/* Badge + Reminder/Join Button */}
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 rounded-full bg-red-200 px-3 py-0.5 text-sm font-bold text-red-600">
          <span className="h-2 w-2 animate-pulse rounded-full bg-red-500"></span>
          {t("LiveNow")}
        </span>
        {event.is_online ? (
          <div className="flex items-center gap-2">
            <button
              onClick={() =>
                handleCopyPath(
                  `${process.env.REACT_APP_BASE_URL}meeting?link=${encodeURIComponent(
                    event.location
                  )}&meetingID=${event.meeting_id}&sessionID=${event.id}&type=2`,
                  event.id
                )
              }
              className="flex items-center gap-2 rounded-full border border-purple-500 bg-white/20 px-4 py-1.5 text-xs font-medium text-white/85 shadow-lg transition hover:bg-white/50 sm:px-5 sm:text-sm"
            >
              <FaCopy /> {copySuccess[event.id] ? t("Copied") : t("CopyLink")}
            </button>
            <button
              onClick={handleClick}
              className="flex items-center gap-2 rounded-full border border-purple-500 bg-white/20 px-4 py-1.5 text-xs font-medium text-white shadow-lg transition hover:bg-white/50 sm:px-5 sm:text-sm"
            >
              <FaVideo size={14} /> {t("JoinEvent")}
            </button>
          </div>
        ) : null}
      </div>

      {/* Content */}
      <div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <div className="flex w-full flex-col overflow-hidden sm:flex-row sm:items-center sm:gap-3">
            <div className="flex items-center gap-2">
              <p className="line-clamp-1 flex-1 text-sm font-bold text-white sm:text-base">{event?.name} </p>
            </div>
          </div>
        </div>
        <p className="mb-2 line-clamp-2 text-sm text-white/70">{event?.description}</p>

        {/* Metadata */}
        <div className="mt-1 space-y-1 text-xs text-gray-200 sm:text-sm">
          {event.is_online ? null : (
            <div className="flex items-center gap-1">
              <FaMapMarkerAlt className="opacity-80" />
              <a
                href={event?.location}
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-white"
              >
                {event?.governorate?.name}
              </a>
            </div>
          )}
          <div className="flex items-center gap-1 text-sm">
            {/* Date with clock icon */}
            <div className="flex items-center gap-1 py-1 font-medium">
              <FaClock className="" />
              <span>{formatCustomDate(event?.start_date)}</span>
            </div>

            {/* Time range */}
            <div className="px-2 py-1">
              {event?.start_time} - {event?.end_time}
            </div>
          </div>

          <div className="flex-wrap items-center gap-x-2 gap-y-1">
            {event?.coaches?.map((item, index) => (
              <span key={index} className="whitespace-nowrap text-xs italic text-purple-100 sm:text-sm">
                {index > 0 && <span className="mx-1 text-purple-400">/</span>}
                {item}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------- Event Card ----------------
const EventCard = ({
  event,
  t,
  loadingEventId,
  handleSetEventReminder,
  handleRemoveEventReminder,
  setSelectedEvent,
  setShowRegisterModal,
}) => {
  // Convert start and end dates to Date objects
  const start = new Date(event?.start_date);
  const end = new Date(event?.end_date);
  const now = new Date();

  const thresholdStart = new Date(start.getTime() - 15 * 60 * 1000);

  // Check if "Join" button should be shown
  const showJoinButton = now >= thresholdStart && now <= end;

  return (
    <div className="flex flex-col rounded-xl bg-purple-50 p-5 ring-1 ring-gray-200 transition">
      {/* Header */}
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <div className="flex w-full flex-col overflow-hidden sm:flex-row sm:items-center sm:gap-3">
            <div className="flex items-center gap-2">
              <p className="line-clamp-1 flex-1 text-sm font-bold text-gray-900 sm:text-base">
                {event?.name}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {event.is_online !== 1 &&
            (event.is_registered ? (
              <span className="rounded-full bg-green-500 px-3 py-2 text-xs font-semibold uppercase text-white">
                {t("youAreRegistered")}
              </span>
            ) : (
              <button
                onClick={() => {
                  setSelectedEvent(event);
                  setShowRegisterModal(true);
                }}
                className="flex items-center gap-2 rounded-full bg-gradient-to-br from-purple-600 to-purple-700 px-4 py-[6.2px] text-xs font-medium text-white shadow-lg hover:opacity-90 sm:px-5 sm:text-sm"
              >
                {t("ConfirmAttendance")}
              </button>
            ))}
          <button
            onClick={
              event.is_reminder
                ? () => handleRemoveEventReminder(event.is_reminder, event.id)
                : () => handleSetEventReminder(event.id)
            }
            disabled={loadingEventId === event.id}
            className={`flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium sm:text-sm ${
              event.is_reminder
                ? "border-purple-200 bg-white text-purple-500 hover:bg-white/80"
                : "border border-purple-500 bg-white/20 text-purple-500 hover:bg-white/50"
            } ${loadingEventId === event.id ? "opacity-50" : ""}`}
          >
            {loadingEventId === event.id ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
            ) : (
              <FaBell size={14} />
            )}
            {event.is_reminder ? t("RemoveReminder") : t("SetReminder")}
          </button>
        </div>
      </div>

      {/* Content */}
      <p className="mb-2 mt-2 line-clamp-2 text-sm text-gray-600">{event?.description}</p>

      {/* Metadata */}
      <div className="text-sm text-gray-500">
        {event.is_online ? null : (
          <div className="flex items-center gap-1">
            <FaMapMarkerAlt className="opacity-70" />
            <a
              href={event?.location}
              target="_blank"
              rel="noopener noreferrer"
              className="text-purple-500 underline hover:text-purple-900"
            >
              {event?.governorate?.name}
            </a>
          </div>
        )}
        <div className="flex items-center gap-1 text-sm text-gray-700">
          {/* Date with clock icon */}
          <div className="flex items-center gap-1 rounded bg-gray-100 py-1 font-medium">
            <FaClock className="text-gray-500" />
            <span>{formatCustomDate(event?.start_date)}</span>
          </div>

          {/* Time range */}
          <div className="rounded bg-gray-100 px-2 py-1 text-gray-600">
            {event?.start_time} - {event?.end_time}
          </div>
        </div>

        <div className="flex-wrap items-center gap-x-2 gap-y-1">
          {event?.coaches?.map((item, index) => (
            <span key={index} className="whitespace-nowrap text-xs italic text-purple-500 sm:text-sm">
              {index > 0 && <span className="mx-1 text-purple-400">/</span>}
              {item}
            </span>
          ))}
        </div>
        {event.is_online ? <p className="text-xs">*{t("upcomingEventsAlert")}</p> : null}
      </div>
    </div>
  );
};

// ---------------- Support Session ----------------
const SupportSessionCard = ({
  session,
  i18n,
  t,
  loadingSessionId,
  handleSetReminder,
  handleRemoveReminder,
}) => (
  <div className="flex flex-col rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 p-5 text-white shadow-md transition hover:shadow-lg">
    {/* Top Row */}
    <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex w-full flex-col overflow-hidden sm:flex-row sm:items-center sm:gap-3">
          <div className="flex items-center gap-2">
            <p className="line-clamp-1 flex-1 text-sm font-bold text-white sm:text-base">
              {getCourseLabel(session?.course_id, i18n.language)}
            </p>
          </div>
        </div>
      </div>

      {/* Reminder Button */}
      <button
        onClick={
          session.is_reminder
            ? () => handleRemoveReminder(session.is_reminder)
            : () => handleSetReminder(session.id)
        }
        disabled={loadingSessionId === session.id}
        className={`flex items-center gap-2 rounded-full border px-4 py-1.5 text-sm font-medium transition-all sm:text-sm ${
          session.is_reminder
            ? "border-purple-200 bg-white text-purple-500 hover:bg-white/80"
            : "border border-purple-500 bg-white/20 text-white hover:bg-white/50"
        } ${loadingSessionId === session.id ? "cursor-not-allowed opacity-50" : ""}`}
      >
        {loadingSessionId === session.id ? (
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
        ) : (
          <FaBell
            size={14}
            className={`transition-colors ${session.is_reminder ? "text-purple-500" : "text-white"}`}
          />
        )}
        {session.is_reminder ? t("RemoveReminder") : t("SetReminder")}
      </button>
    </div>

    {/* Description */}
    <p className="mb-2 line-clamp-2 text-sm text-white/70">{session.description}</p>
    <div>
      {/* Date */}
      <div className="flex items-center gap-1 text-sm text-white/60">
        {/* Date with clock icon */}
        <div className="flex items-center gap-1 py-1 font-medium">
          <FaClock />
          <span>{formatCustomDate(session?.start_date)}</span>
        </div>

        {/* Time range */}
        <div className="px-2 py-1">
          {session?.start_time} - {session?.end_time}
        </div>
      </div>

      <div className="flex-wrap items-center gap-x-2 gap-y-1">
        {session?.coaches?.map((item, index) => (
          <span key={index} className="whitespace-nowrap text-xs italic text-purple-100 sm:text-sm">
            {index > 0 && <span className="mx-1 text-purple-400">/</span>}
            {item}
          </span>
        ))}
      </div>
      <p className="text-xs">*{t("upcomingSessionsAlert")}</p>
    </div>
  </div>
);

// ---------------- Summary ----------------
const SummaryCard = ({ session, i18n }) => (
  <div className="flex flex-col rounded-xl bg-gradient-to-br from-indigo-700 to-purple-800 p-4 text-white shadow-md transition hover:shadow-lg sm:p-5">
    {/* Top Row: Course + Coaches + Icons */}
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
      <span className="flex items-center gap-2 truncate text-sm font-bold sm:text-base">
        {getCourseLabel(session?.course_id, i18n.language)}
      </span>

      <div className="flex items-center gap-3 text-lg sm:text-xl">
        {session.video && <DownloadButton url={session.video} name={session.name} />}
        {session.document && <DownloadButton url={session.document} name={session.name} />}
      </div>
    </div>

    {/* Description */}
    <p className="mb-2 mt-2 line-clamp-2 text-sm text-indigo-200 sm:text-sm">{session.description}</p>
    {session?.coaches?.map((item, index) => (
      <span key={index} className="text-xs italic text-purple-100 sm:text-sm">
        {index > 0 && <span className="mx-1 text-purple-200">/</span>}
        {item}
      </span>
    ))}
  </div>
);

const DownloadButton = ({ url, name }) => {
  const handleDownload = async () => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();

      // Extract extension safely
      const extension = url.split(".").pop().split(/\#|\?/)[0];
      const fileName = `${(name || "file").replace(/\s+/g, "_")}.${extension}`;

      // Create a temporary link
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error("Download failed", err);
    }
  };

  // Pick icon based on file type
  const extension = url.split(".").pop().split(/\#|\?/)[0].toLowerCase();
  const isVideo = ["mp4", "mov", "avi", "mkv", "webm"].includes(extension);

  return (
    <button onClick={handleDownload} className="transition hover:text-indigo-300">
      {isVideo ? <FaVideo /> : <FaFile />}
    </button>
  );
};

export default function Home() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { t, i18n } = useTranslation();

  const [liveSessions, setLiveSessions] = useState([]);
  const [upcomingSessions, setUpcomingSessions] = useState([]);
  const [summarySessions, setSummarySessions] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [liveEvents, setLiveEvents] = useState([]);
  const [loadingEventId, setLoadingEventId] = useState(null);
  const [loadingSessionId, setLoadingSessionId] = useState(null);
  const { fetchReminders } = useReminders();
  const [copySuccess, setCopySuccess] = useState({});
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupType, setPopupType] = useState("success");
  const [courseCertificates, setCourseCertificates] = useState({});
  const [showAssessmentModal, setShowAssessmentModal] = useState(false);
  const [showReadyAssessmentModal, setShowReadyAssessmentModal] = useState(false);
  const [submittedAssessments, setSubmittedAssessments] = useState([]);
  const [readyAssessments, setReadyAssessments] = useState([]);
  const [loadingAssessments, setLoadingAssessments] = useState(true);
  const [submittingAssessments, setSubmittingAssessments] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [loadingLocalCourses, setLoadingLocalCourses] = useState(true);
  const [showTrainerModal, setShowTrainerModal] = useState(false);

  const [localCourses, setLocalCourses] = useState([]);

  const [loading, setLoading] = useState({
    live: true,
    upcoming: true,
    summary: true,
    events: true,
    liveEvents: true,
  });
  const [error, setError] = useState({
    live: null,
    upcoming: null,
    summary: null,
    events: null,
    liveEvents: null,
  });

  const handleSubmitAssessment = async (selectedCourses, courseCertificates) => {
    setSubmittingAssessments(true);
    try {
      // Block if no courses are selected
      if (!acceptedTerms) {
        setPopupMessage(t("PleaseAcceptTerms"));
        setPopupType("error");
        setTimeout(() => setPopupMessage(""), 3000);
        setSubmittingAssessments(false);
        return;
      }

      // Block if no courses are selected
      if (!selectedCourses || selectedCourses.length === 0) {
        setPopupMessage(t("Pleaseselectatleastonecourse"));
        setPopupType("error");
        setTimeout(() => setPopupMessage(""), 3000);
        setSubmittingAssessments(false);
        return;
      }

      // Check for missing certificates
      const missingCertificates = selectedCourses.filter((id) => !courseCertificates[id]);
      if (missingCertificates.length > 0) {
        setPopupMessage(t("Pleaseattachacertificateforallselectedcourses"));
        setPopupType("error");
        setTimeout(() => setPopupMessage(""), 3000);
        setSubmittingAssessments(false);
        return;
      }

      // Submit all courses
      for (const courseId of selectedCourses) {
        const formData = new FormData();
        formData.append("course_id", courseId);
        formData.append("certificate", courseCertificates[courseId]);

        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/user-assessments`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
            lang: i18n.language,
            Authorization: `Bearer ${user?.token}`,
          },
        });
      }

      setPopupMessage(t("Yourapplicationhasbeensuccessfullysubmitted"));
      setPopupType("success");

      // Reset states
      setShowAssessmentModal(false);
      setSelectedCourses([]);
      setCourseCertificates({});
      setSubmittingAssessments(false);
      fetchSubmittedAssessments();

      setTimeout(() => setPopupMessage(""), 3000);
    } catch (error) {
      console.error("Error submitting assessment:", error.response?.data?.message || error.message);
      setPopupMessage(error.response?.data?.message || t("An error occurred"));
      setPopupType("error");
      setTimeout(() => setPopupMessage(""), 3000);
      setSubmittingAssessments(false);
    }
  };

  const isWithinSchedule = (schedule) => {
    if (!schedule) return false;

    const now = new Date();
    const start = new Date(schedule.start_date.replace(" ", "T"));
    const end = new Date(schedule.end_date.replace(" ", "T"));

    return now >= start && now <= end;
  };

  const formatSchedule = (schedule) => {
    if (!schedule) return "";

    const format = (dateStr) => {
      const d = new Date(dateStr.replace(" ", "T"));

      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");

      return `${year}/${month}/${day} ${hours}:${minutes}`;
    };

    return i18n.language === "ar"
      ? `من ${format(schedule.start_date)} إلى ${format(schedule.end_date)}`
      : `From ${format(schedule.start_date)} to ${format(schedule.end_date)}`;
  };

  const fetchLocalCourses = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/courses`, {
        headers: {
          lang: i18n.language,
          Authorization: `Bearer ${user?.token}`,
        },
      });
      setLocalCourses(response?.data?.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        // Unauthorized: remove user and redirect to login
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        setError("Failed to load events. Please try again later.");
        console.error(err);
      }
    } finally {
      setLoadingLocalCourses(false);
    }
  };

  const fetchSubmittedAssessments = async () => {
    setLoadingAssessments(true);

    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/assessments-user-submitted`, {
        headers: {
          lang: i18n.language,
          Authorization: `Bearer ${user?.token}`,
        },
      });
      setSubmittedAssessments(response?.data?.data?.courses || []);
      setLoadingAssessments(false);
    } catch (err) {
      setLoadingAssessments(false);

      if (err.response?.status === 401) {
        // Unauthorized: remove user and redirect to login
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        setError("Failed to load events. Please try again later.");
        console.error(err);
      }
    } finally {
      setLoadingAssessments(false);
    }
  };
  const fetchReadyAssessments = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/user-assessments`, {
        headers: {
          lang: i18n.language,
          Authorization: `Bearer ${user?.token}`,
        },
      });
      setReadyAssessments(response?.data?.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        // Unauthorized: remove user and redirect to login
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        setError("Failed to load events. Please try again later.");
        console.error(err);
      }
    }
  };

  const fetchSessions = async (type, setter, loadingKey, errorKey) => {
    setLoading((prev) => ({ ...prev, [loadingKey]: true }));
    setError((prev) => ({ ...prev, [errorKey]: null }));
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/sessions`, {
        params: { type, per_page: 1 },
        headers: {
          lang: i18n.language,
          Authorization: `Bearer ${user?.token}`,
        },
      });
      setter(response?.data?.data?.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        // Unauthorized: remove user and redirect to login
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        setError("Failed to load events. Please try again later.");
        console.error(err);
      }
    } finally {
      setLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  const handleSubmitCourseSelection = async () => {
    if (!selectedEvent) return;

    if (selectedCourses.length === 0) {
      setPopupMessage(t("PleaseSelectAtLeastOneCourse"));
      setPopupType("error");
      setTimeout(() => setPopupMessage(""), 3000);
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/event-register/${selectedEvent.id}`,
        {
          courses: selectedCourses, // sends array of selected IDs
        },
        {
          headers: {
            lang: i18n.language,
            Authorization: `Bearer ${user?.token}`,
          },
        }
      );

      setPopupMessage(t("RegistrationSuccessful"));
      setPopupType("success");
      setTimeout(() => setPopupMessage(""), 3000);

      // update event to show registered
      setUpcomingEvents((prev) =>
        prev.map((ev) => (ev.id === selectedEvent.id ? { ...ev, is_registered: true } : ev))
      );
    } catch (err) {
      console.error(err);
      setPopupMessage(t("ErrorOccurred"));
      setPopupType("error");
      setTimeout(() => setPopupMessage(""), 3000);
    }

    setShowRegisterModal(false);
    setSelectedEvent(null);
    setSelectedCourses([]);
  };

  const fetchEvents = async () => {
    setLoading((prev) => ({ ...prev, events: true }));
    setError((prev) => ({ ...prev, events: null }));
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/events`, {
        params: { type: "upcoming", per_page: 1 },
        headers: {
          lang: i18n.language,
          Authorization: `Bearer ${user?.token}`,
        },
      });
      setUpcomingEvents(response?.data?.data?.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        // Unauthorized: remove user and redirect to login
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        setError("Failed to load events. Please try again later.");
        console.error(err);
      }
    } finally {
      setLoading((prev) => ({ ...prev, events: false }));
    }
  };

  const fetchLiveEvents = async () => {
    setLoading((prev) => ({ ...prev, liveEvents: true }));
    setError((prev) => ({ ...prev, liveEvents: null }));
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/events`, {
        params: { type: "live", per_page: 1 },
        headers: {
          lang: i18n.language,
          Authorization: `Bearer ${user?.token}`,
        },
      });
      setLiveEvents(response?.data?.data?.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        setError("Failed to load live events. Please try again later.");
        console.error(err);
      }
    } finally {
      setLoading((prev) => ({ ...prev, liveEvents: false }));
    }
  };

  const handleLiveSessionClick = (session) => {
    navigate(
      `/meeting?link=${encodeURIComponent(session.link)}&meetingID=${session.meeting_id}&sessionID=${session.id}&type=1`
    );
  };
  const handleEventClick = (event) => {
    navigate(
      `/meeting?link=${encodeURIComponent(event.location)}&meetingID=${event.meeting_id}&sessionID=${event.id}&type=2`
    );
  };

  const handleSetReminder = async (sessionId) => {
    setLoadingSessionId(sessionId);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reminder`,
        {
          type: 2, // type 2 = sessions
          model_id: sessionId,
        },
        {
          headers: {
            lang: i18n.language,
            Authorization: `Bearer ${user?.token}`,
          },
        }
      );

      const reminderId = response?.data?.data?.id; // get reminder ID from backend

      // ✅ Update UI immediately
      setUpcomingSessions((prevSessions) =>
        prevSessions.map((s) => (s.id === sessionId ? { ...s, is_reminder: reminderId } : s))
      );
      fetchReminders();
    } catch (err) {
      if (err.response?.status === 401) {
        // Unauthorized: remove user and redirect to login
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        setError("Failed to load events. Please try again later.");
        console.error(err);
      }
    } finally {
      setLoadingSessionId(null);
    }
  };

  const handleRemoveReminder = async (reminderId, sessionId) => {
    setLoadingSessionId(sessionId);

    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/reminder/${reminderId}`, {
        headers: {
          lang: i18n.language,
          Authorization: `Bearer ${user?.token}`,
        },
      });

      // ✅ Update UI immediately
      setUpcomingSessions((prevSessions) =>
        prevSessions.map((s) => (s.is_reminder === reminderId ? { ...s, is_reminder: null } : s))
      );
      fetchReminders();
    } catch (err) {
      if (err.response?.status === 401) {
        // Unauthorized: remove user and redirect to login
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        setError("Failed to load events. Please try again later.");
        console.error(err);
      }
    } finally {
      setLoadingSessionId(null);
    }
  };

  const handleSetEventReminder = async (eventId) => {
    setLoadingEventId(eventId);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/reminder`,
        { type: 1, model_id: eventId },
        { headers: { lang: i18n.language, Authorization: `Bearer ${user?.token}` } }
      );

      const reminderId = response?.data?.data?.id;
      setUpcomingEvents((prev) =>
        prev.map((ev) => (ev.id === eventId ? { ...ev, is_reminder: reminderId } : ev))
      );

      fetchReminders();
    } catch (err) {
      if (err.response?.status === 401) {
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        setError("Failed to load events. Please try again later.");
        console.error(err);
      }
    } finally {
      setLoadingEventId(null);
    }
  };

  const handleRemoveEventReminder = async (reminderId, eventId) => {
    setLoadingEventId(eventId);
    try {
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/reminder/${reminderId}`, {
        headers: { lang: i18n.language, Authorization: `Bearer ${user?.token}` },
      });

      setUpcomingEvents((prev) =>
        prev.map((ev) => (ev.is_reminder === reminderId ? { ...ev, is_reminder: null } : ev))
      );

      fetchReminders();
    } catch (err) {
      if (err.response?.status === 401) {
        setUser(null);
        navigate("/auth/login", { replace: true });
      } else {
        setError("Failed to load events. Please try again later.");
        console.error(err);
      }
    } finally {
      setLoadingEventId(null);
    }
  };

  const handleCopyPath = (path, eventId) => {
    const textarea = document.createElement("textarea");
    textarea.value = path;
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand("copy");

      // set only for this session
      setCopySuccess((prev) => ({ ...prev, [eventId]: true }));

      setTimeout(() => {
        setCopySuccess((prev) => ({ ...prev, [eventId]: false }));
      }, 1500);
    } catch (err) {
      console.error("Copy failed", err);
    }
    document.body.removeChild(textarea);
  };

  useEffect(() => {
    if (!user?.token) return;
    fetchLocalCourses();
    fetchSubmittedAssessments();
    fetchReadyAssessments();
    fetchSessions("live", setLiveSessions, "live", "live");
    fetchSessions("upcoming", setUpcomingSessions, "upcoming", "upcoming");
    fetchSessions("summary", setSummarySessions, "summary", "summary");
    fetchEvents();
    fetchLiveEvents();
  }, [user?.token, i18n.language]);

  const staticCourses = useMemo(
    () => [
      
    ],
    []
  );

  return (
    <>
      <div className="mx-auto max-w-6xl space-y-5 p-4 pt-0" dir={i18n.language === "ar" ? "rtl" : "ltr"}>
        {/* {submittedAssessments.length ? (
          <div className="flex w-full flex-col items-center justify-between gap-4 rounded-xl bg-gradient-to-r from-purple-600 to-purple-400 px-6 py-4 shadow-lg md:flex-row">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center rounded-full text-yellow-300">
                <FaExclamationCircle size={26} />
              </div>

              <div className="flex flex-col">
                <h2 className="text-lg font-bold text-white">
                  {i18n.language === "ar" ? "لا تنسى إجراء التقييم" : "Don't forget to take your assessment"}
                </h2>
                <p className="text-sm text-white/90">
                  {i18n.language === "ar"
                    ? "اختر الدورات التي أكملتها وأرفق الشهادات لإجراء التقييم الآن."
                    : "Select the courses you’ve completed and attach certificates to take your assessment now."}
                </p>
              </div>
            </div>

            <button
              onClick={() => {
                fetchSubmittedAssessments();
                setSelectedCourses([]);
                setCourseCertificates({});
                setShowAssessmentModal(true);
              }}
              className="flex items-center gap-2 rounded-full bg-yellow-300 px-6 py-2 font-semibold text-purple-700 transition hover:bg-yellow-400"
            >
              {i18n.language === "ar" ? "ابدأ التقييم" : "Start Assessment"}
              {i18n.language === "ar" ? <FaChevronLeft /> : <FaChevronRight />}
            </button>
          </div>
        ) : null} */}

        {/* {readyAssessments.length > 0 && (
          <div className="flex w-full flex-col items-center justify-between gap-4 rounded-xl bg-gradient-to-r from-purple-600 to-purple-400 px-6 py-4 shadow-lg md:flex-row">
            <div className="flex items-center gap-4">
              <FaExclamationCircle className="text-yellow-300" size={26} />

              <div>
                <h2 className="text-lg font-bold text-white">
                  {i18n.language === "ar" ? "لديك تقييمات جاهزة" : "You have ready assessments"}
                </h2>
                <p className="text-sm text-white/90">
                  {i18n.language === "ar"
                    ? "اضغط لعرض التقييمات وبدء التقييم."
                    : "Click to view your ready assessments and start."}
                </p>
              </div>
            </div>

            <button
              onClick={() => setShowReadyAssessmentModal(true)}
              className="flex items-center gap-2 rounded-full bg-yellow-300 px-6 py-2 font-semibold text-purple-700 transition hover:bg-yellow-400"
            >
              {i18n.language === "ar" ? "عرض التقييمات" : "View Assessments"}
              {i18n.language === "ar" ? <FaChevronLeft /> : <FaChevronRight />}
            </button>
          </div>
        )} */}
        {/* Trainer Registration Banner — hidden */}
        {/* Branding / Hero */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-5">
            <div>
              <div className="flex items-center gap-5">
                <img src="/logo-blue.svg" alt="Logo" className="h-24 w-24" />
              </div>

              <p className="mt-1 text-base font-normal text-gray-600 sm:text-2xl">
                {t("selectOneOfCoursesBelowToStartYourLearningJourney")}
              </p>
            </div>
          </div>
          <div className="hidden justify-end lg:flex"></div>
        </div>

        <section>
          {loadingLocalCourses ? (
            <div className="flex items-center justify-center py-10">
              <svg
                className="h-10 w-10 animate-spin text-purple-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
                <path fill="currentColor" className="opacity-75" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
              {/* 🔹 Local Courses */}
              {localCourses.map((course) => (
                <CourseCard
                  key={`local-${course.id}`}
                  t={t}
                  title={course.name}
                  icon={course.icon}
                  imageSrc={course.image}
                  description={course.short_description}
                  isStarted={course.is_started}
                  isCompleted={course.is_completed}
                  isSurveyCompleted={course.is_survey_completed}
                  localLink={() => {
                    localStorage.setItem("lang", i18n.language);
                    navigate(`/course-overview/${course?.id}`);
                  }}
                />
              ))}
              {staticCourses.map((course) => (
                <CourseCard key={`static-${course.title}`} t={t} {...course} onOpen={setSelectedCourse} />
              ))}
            </div>
          )}
        </section>

        {/* Online Support Sessions (Live) */}
        {liveSessions.length > 0 && (
          <section>
            <SectionHeader
              title="OnlineSupportSessions"
              onAction={() => navigate("/support-sessions?type=live")}
              i18n={i18n}
              t={t}
            />
            {loading.live ? (
              <div className="flex flex-col items-center justify-center py-6">
                <div className="h-6 w-6 animate-spin rounded-full border-4 border-gray-200 border-t-purple-600"></div>
              </div>
            ) : error.live ? (
              <p className="text-red-500">{error.live}</p>
            ) : (
              <div className="grid gap-3">
                {liveSessions.map((s) => (
                  <LiveSessionCard
                    key={s.id}
                    session={s}
                    t={t}
                    i18n={i18n}
                    handleClick={() => handleLiveSessionClick(s)}
                    handleCopyPath={handleCopyPath}
                    copySuccess={copySuccess}
                  />
                ))}
              </div>
            )}
          </section>
        )}
        {/* Live Events */}
        {liveEvents.length > 0 && (
          <section>
            <SectionHeader
              title="OnlineEvents"
              onAction={() => navigate("/events?type=live")}
              i18n={i18n}
              t={t}
            />
            {loading.liveEvents ? (
              <div className="flex flex-col items-center justify-center py-6">
                <div className="h-6 w-6 animate-spin rounded-full border-4 border-gray-200 border-t-purple-600"></div>
              </div>
            ) : error.liveEvents ? (
              <p className="text-red-500">{error.liveEvents}</p>
            ) : (
              <div className="grid gap-3">
                {liveEvents.map((e) => (
                  <LiveEventCard
                    key={e.id}
                    event={e}
                    t={t}
                    i18n={i18n}
                    loadingEventId={loadingEventId}
                    handleSetEventReminder={handleSetEventReminder}
                    handleRemoveEventReminder={handleRemoveEventReminder}
                    handleClick={() => handleEventClick(e)}
                    handleCopyPath={handleCopyPath}
                    copySuccess={copySuccess}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Upcoming Events */}
        {upcomingEvents.length > 0 && (
          <section>
            <SectionHeader
              title="UpcomingEvents"
              onAction={() => navigate("/events?type=upcoming")}
              i18n={i18n}
              t={t}
            />
            {loading.events ? (
              <div className="flex flex-col items-center justify-center py-6">
                <div className="h-6 w-6 animate-spin rounded-full border-4 border-gray-200 border-t-purple-600"></div>
              </div>
            ) : error.events ? (
              <p className="text-red-500">{error.events}</p>
            ) : (
              <div className="grid gap-4">
                {upcomingEvents.map((e) => (
                  <EventCard
                    key={e.id}
                    event={e}
                    t={t}
                    i18n={i18n}
                    loadingEventId={loadingEventId}
                    handleSetEventReminder={handleSetEventReminder}
                    handleRemoveEventReminder={handleRemoveEventReminder}
                    handleClick={() => handleEventClick(e)}
                    setShowRegisterModal={setShowRegisterModal}
                    setSelectedEvent={setSelectedEvent}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Upcoming Online Support Sessions */}
        {upcomingSessions.length > 0 && (
          <section>
            <SectionHeader
              title="UpcomingOnlineSupportSessions"
              onAction={() => navigate("/support-sessions?type=upcoming")}
              i18n={i18n}
              t={t}
            />
            {loading.upcoming ? (
              <div className="flex flex-col items-center justify-center py-6">
                <div className="h-6 w-6 animate-spin rounded-full border-4 border-gray-200 border-t-purple-600"></div>
              </div>
            ) : error.upcoming ? (
              <p className="text-red-500">{error.upcoming}</p>
            ) : (
              <div className="space-y-3">
                {upcomingSessions.map((s) => (
                  <SupportSessionCard
                    key={s.id}
                    session={s}
                    t={t}
                    i18n={i18n}
                    loadingSessionId={loadingSessionId}
                    handleSetReminder={handleSetReminder}
                    handleRemoveReminder={handleRemoveReminder}
                  />
                ))}
              </div>
            )}
          </section>
        )}

        {/* Previous Sessions Summary */}
        {summarySessions.length > 0 && (
          <section>
            <SectionHeader
              title="PreviousSessionsSummary"
              onAction={() => navigate("/support-sessions?type=summary")}
              i18n={i18n}
              t={t}
            />
            {loading.summary ? (
              <div className="flex flex-col items-center justify-center py-6">
                <div className="h-6 w-6 animate-spin rounded-full border-4 border-gray-200 border-t-purple-600"></div>
              </div>
            ) : error.summary ? (
              <p className="text-red-500">{error.summary}</p>
            ) : (
              <div className="space-y-3">
                {summarySessions.map((s) => (
                  <SummaryCard key={s.id} i18n={i18n} session={s} />
                ))}
              </div>
            )}
          </section>
        )}
      </div>
      {showRegisterModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-bold text-purple-700">{t("SelectCourses")}</h2>

            {/* Courses List */}
            <div className="mb-4 max-h-64 space-y-3 overflow-y-auto">
              {courses.map((course) => (
                <label
                  key={course.id}
                  className="flex cursor-pointer items-center gap-3 rounded-lg border p-3 hover:bg-purple-50"
                >
                  <input
                    type="checkbox"
                    checked={selectedCourses.includes(course.id)}
                    onChange={() => {
                      setSelectedCourses((prev) =>
                        prev.includes(course.id) ? prev.filter((c) => c !== course.id) : [...prev, course.id]
                      );
                    }}
                    className="h-4 w-4 text-purple-600 accent-purple-600"
                  />
                  <span className="text-sm">{i18n.language === "ar" ? course.name_ar : course.name_en}</span>
                </label>
              ))}
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRegisterModal(false);
                  setSelectedCourses([]);
                }}
                className="rounded-full border border-gray-300 px-4 py-2 text-sm hover:bg-gray-100"
              >
                {t("Cancel")}
              </button>

              <button
                onClick={() => handleSubmitCourseSelection()}
                className="rounded-full bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
              >
                {t("Confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
      {showAssessmentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="relative max-h-[80vh] w-full max-w-md overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
            <h2 className="mb-2 text-lg font-bold text-purple-700">
              {i18n.language === "ar" ? "اختر الدورات لإجراء التقييم" : "Select Courses for Assessment"}
            </h2>
            <p className="mb-4 text-sm text-gray-600">
              {i18n.language === "ar"
                ? "يرجى اختيار الدورات التي أكملتها وإرفاق الشهادات الخاصة بها للتحقق من إتمامك."
                : "Please select the courses you have completed and attach the corresponding certificates to verify your completion."}
            </p>

            {/* Loader overlay */}
            {loadingAssessments && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70 backdrop-blur-sm">
                <svg
                  className="h-10 w-10 animate-spin text-purple-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  ></path>
                </svg>
              </div>
            )}

            {/* Courses List */}
            <div className="mb-4 space-y-3">
              {!loadingAssessments && submittedAssessments.length == 0 ? (
                <p className="text-center text-gray-500">
                  {i18n.language === "ar" ? "لا توجد تقييمات لتقديمها" : "No assessments to submit"}
                </p>
              ) : (
                courses
                  .filter((course) => submittedAssessments.includes(course.id))
                  .map((course) => {
                    const isSelected = selectedCourses.includes(course.id);

                    return (
                      <div key={course.id} className="rounded-lg border p-3 hover:bg-purple-50">
                        <label className="flex cursor-pointer items-center gap-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() =>
                              setSelectedCourses((prev) =>
                                prev.includes(course.id)
                                  ? prev.filter((c) => c !== course.id)
                                  : [...prev, course.id]
                              )
                            }
                            className="h-4 w-4 text-purple-600 accent-purple-600"
                          />
                          <span className="text-sm">
                            {i18n.language === "ar" ? course.name_ar : course.name_en}
                          </span>
                        </label>

                        {/* File upload */}
                        {isSelected && (
                          <label className="mt-2 flex h-16 w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-purple-300 transition hover:border-purple-500 hover:bg-purple-50">
                            <span className="text-sm text-gray-600">
                              {courseCertificates[course.id]
                                ? courseCertificates[course.id].name
                                : i18n.language === "ar"
                                  ? "إرفاق الشهادة"
                                  : "Attach certificate"}
                            </span>
                            <input
                              type="file"
                              accept=".pdf"
                              onChange={(e) =>
                                setCourseCertificates((prev) => ({
                                  ...prev,
                                  [course.id]: e.target.files[0],
                                }))
                              }
                              className="hidden"
                            />
                          </label>
                        )}
                      </div>
                    );
                  })
              )}
            </div>

            {/* Terms & Conditions */}
            <div className="mb-4 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="terms"
                  checked={acceptedTerms}
                  onChange={() => setAcceptedTerms(!acceptedTerms)}
                  className="h-5 w-5 accent-blue-600"
                />
                <div className="flex items-center gap-1 font-bold">
                  <span htmlFor="terms" className="cursor-pointer text-sm">
                    {i18n.language === "ar" ? "أوافق على" : "I agree to the"}
                  </span>
                  <label
                    htmlFor="terms"
                    className="cursor-pointer text-sm text-blue-700 underline"
                    onClick={(e) => {
                      e.preventDefault();
                      setShowTermsModal(true);
                    }}
                  >
                    {i18n.language === "ar" ? "الشروط والأحكام" : "Terms and Conditions"}
                  </label>
                </div>
              </div>

              {/* Hint */}
              <p className="mt-1 text-xs text-gray-500">
                {i18n.language === "ar"
                  ? "يجب الموافقة على الشروط قبل المتابعة"
                  : "You must accept the terms before continuing"}
              </p>
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowAssessmentModal(false);
                  setSelectedCourses([]);
                  setCourseCertificates({});
                }}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-100"
              >
                {i18n.t("Cancel")}
              </button>

              <button
                onClick={() => handleSubmitAssessment(selectedCourses, courseCertificates)}
                disabled={submittingAssessments || selectedCourses.length === 0 || !acceptedTerms}
                className={`relative flex w-24 items-center justify-center rounded-lg px-4 py-2 text-sm font-medium text-white ${
                  submittingAssessments || selectedCourses.length === 0 || !acceptedTerms
                    ? "cursor-not-allowed bg-purple-400"
                    : "bg-purple-600 hover:bg-purple-700"
                }`}
              >
                {submittingAssessments ? (
                  <span className="flex items-center gap-2">
                    <svg
                      className="h-4 w-4 animate-spin text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                      ></path>
                    </svg>
                  </span>
                ) : (
                  i18n.t("Confirm")
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {showReadyAssessmentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-2xl bg-white p-8 shadow-2xl">
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-800">
                {i18n.language === "ar" ? "التقييمات الجاهزة" : "Ready Assessments"}
              </h3>

              <button
                onClick={() => setShowReadyAssessmentModal(false)}
                className="rounded-full bg-gray-100 px-3 py-1 text-sm font-medium text-gray-600 hover:bg-gray-200"
              >
                ✕
              </button>
            </div>
            <h2 className="mb-6 text-3xl font-extrabold text-gray-800">{t("competition_overview")}</h2>
            <p className="mb-6 text-sm text-gray-600 sm:text-base">
              {t("competition_intro", { count: 15, minutes: 30 })}
            </p>
            <ul className="mb-8 space-y-3 text-left text-gray-600">
              <li className="flex items-start gap-2">
                <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-purple-600"></span>
                {t("questions_count", { count: 15 })}
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-purple-600"></span>
                {t("total_time", { minutes: 30 })}
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-purple-600"></span>
                {t("one_attempt")}
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-purple-600"></span>
                {t("camera_must_remain_on")}
              </li>
              <li className="flex items-start gap-2">
                <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-purple-600"></span>
                {t("session_recorded")}
              </li>
            </ul>
            {/* Exams List */}
            <ul className="space-y-4">
              {readyAssessments.map((item) => {
                const enabled = isWithinSchedule(item.next_exam_schedule);

                return (
                  <li key={item.id} className="rounded-xl border border-gray-200 p-5">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      {/* Course Info */}
                      <div className="space-y-1">
                        <h4 className="text-lg font-semibold text-gray-800">{item.course}</h4>

                        <span
                          className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${
                            enabled ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {enabled
                            ? i18n.language === "ar"
                              ? "متاح الآن"
                              : "Available Now"
                            : i18n.language === "ar"
                              ? "غير متاح حالياً"
                              : "Not Available Yet"}
                        </span>
                      </div>

                      {/* Action */}
                      <button
                        disabled={!enabled}
                        onClick={() => {
                          if (item.next_exam_schedule?.end_date) {
                            localStorage.setItem("assessment_end_date", item.next_exam_schedule.end_date);
                          }

                          navigate(`/assessment/${item.id}`);
                        }}
                        className={`min-w-[160px] rounded-xl px-6 py-3 text-sm font-semibold text-white transition ${
                          enabled ? "bg-purple-600 hover:bg-purple-700" : "cursor-not-allowed bg-gray-400"
                        }`}
                      >
                        {i18n.language === "ar" ? "ابدأ التقييم" : "Start Assessment"}
                      </button>
                    </div>

                    {/* Schedule Hint */}
                    {!enabled && item.next_exam_schedule && (
                      <div className="mt-4 flex items-center gap-1 rounded-lg bg-gray-50 p-3 text-sm text-gray-600">
                        <FaClock className="text-purple-500" />
                        {i18n.language === "ar"
                          ? `موعد التقييم ${formatSchedule(item.next_exam_schedule)}`
                          : `Exam schedule ${formatSchedule(item.next_exam_schedule)}`}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            {/* Footer */}
            <div className="mt-8 text-right">
              <button
                onClick={() => setShowReadyAssessmentModal(false)}
                className="rounded-lg bg-gray-100 px-6 py-2 font-semibold text-gray-700 hover:bg-gray-200"
              >
                {i18n.language === "ar" ? "إغلاق" : "Close"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTermsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="relative max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
            <h2 className="mb-4 text-lg font-bold text-purple-700">
              {i18n.language === "ar" ? "الشروط والأحكام" : "Terms and Conditions"}
            </h2>

            <div className="mb-4 space-y-3 text-sm text-gray-600">
              {i18n.language === "ar" ? (
                <div className="space-y-2 text-right">
                  <p>بمشاركتك في هذه المسابقة، فإنك توافق على ما يلي:</p>
                  <ul className="list-inside list-disc">
                    <li>
                      جميع معلومات الشهادات المقدمة دقيقة وأصيلة، وقد تتحقق JoCodes من شهادتك مع Udacity إذا
                      لزم الأمر.
                    </li>
                    <li>
                      ستكمل المسابقة بشكل مستقل، دون مساعدة من أي شخص آخر أو أدوات خارجية أو أنظمة ذكاء
                      اصطناعي أو بحث على الإنترنت.
                    </li>
                    <li>
                      يجب أن تظل الكاميرا تعمل طوال المسابقة، وقد يتم تسجيل جلستك للتحقق من الهوية ومنع الغش.
                    </li>
                    <li>
                      قد تقوم JoCodes بمراجعة التسجيل واستبعاد أي مشارك يظهر سلوكًا مشبوهًا أو غير نزيه (مثل
                      إخفاء الكاميرا، أو تبديل التبويبات، أو الحصول على مساعدة خارجية).
                    </li>
                    <li>
                      أنت مسؤول عن التأكد من أن اتصال الإنترنت وجهازك والكاميرا تعمل بشكل صحيح أثناء المسابقة؛
                      المشاكل التقنية لا تمنح محاولات إضافية.
                    </li>
                    <li>
                      قد تستخدم JoCodes اسمك وإنجازك للإعلان عن الفائزين، أو لأغراض التعرف أو المواد
                      التسويقية.
                    </li>
                    <li>
                      قد تقوم JoCodes بتحديث أو تعديل أو إلغاء قواعد المسابقة في أي وقت لضمان العدالة والحفاظ
                      على نزاهة المسابقة.
                    </li>
                  </ul>
                </div>
              ) : (
                <div className="space-y-2 text-left">
                  <p>By entering this competition, you agree that:</p>
                  <ul className="list-inside list-disc">
                    <li>
                      All submitted certificate information is accurate and authentic, and JoCodes may verify
                      your certificate with Udacity if needed.
                    </li>
                    <li>
                      You will complete the competition independently, without help from another person,
                      external tools, AI systems, or online search.
                    </li>
                    <li>
                      Your camera must remain on throughout the competition, and your session may be recorded
                      for identity verification and anti-cheating purposes.
                    </li>
                    <li>
                      JoCodes may review the recording and disqualify any participant showing suspicious or
                      dishonest behavior (such as camera obstruction, tab switching, or external assistance).
                    </li>
                    <li>
                      You are responsible for ensuring your internet connection, device, and camera function
                      properly during the competition; technical issues do not grant extra attempts.
                    </li>
                    <li>
                      JoCodes may use your name and achievement for winner announcements, recognition, or
                      marketing materials.
                    </li>
                    <li>
                      JoCodes may update, modify, or cancel the competition rules at any time to ensure
                      fairness and maintain competition integrity.
                    </li>
                  </ul>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowTermsModal(false)}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm hover:bg-gray-100"
              >
                {t("close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {popupMessage && (
        <div
          className={`fixed bottom-5 left-1/2 z-50 w-auto max-w-md -translate-x-1/2 rounded-lg px-4 py-3 text-white shadow-lg transition-all duration-300 ${
            popupType === "success" ? "bg-green-600" : "bg-red-600"
          }`}
        >
          {popupMessage}
        </div>
      )}
      {selectedCourse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm">
          <div className="relative w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-[0_20px_60px_rgba(0,0,0,0.2)]">
            {/* Top Accent */}
            <div className="h-2 w-full bg-gradient-to-r from-purple-500 via-pink-500 to-indigo-500" />

            {/* Close Button */}
            <button
              onClick={() => setSelectedCourse(null)}
              className="absolute right-4 top-4 rounded-full p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
            >
              <HiX size={22} />
            </button>

            {/* Content */}
            <div className="flex flex-col items-center gap-6 p-6 text-center md:p-8">
              {/* Title */}
              <h2 className="text-xl font-bold text-gray-900 md:text-2xl">
                {i18n.language === "ar"
                  ? "نستعد لإطلاق إصدار محدث من JoCodes "
                  : "We are preparing to launch an updated version of JoCodes "}
              </h2>

              {/* Description */}
              <p className="max-w-md text-sm leading-relaxed text-gray-600 md:text-base">
                {i18n.language === "ar"
                  ? "تم إيقاف التسجيل مؤقتًا لتحديث وتطوير تجربة التعلّم."
                  : "Registration has been temporarily suspended to update and improve the learning experience."}
              </p>

              {/* Warning / Info Box */}
              {selectedCourse.warningMessage && (
                <div className="w-full rounded-2xl border border-purple-100 bg-purple-50 p-4 text-center md:p-5">
                  <p className="text-sm leading-relaxed text-purple-700">
                    {t(selectedCourse.warningMessage)}
                  </p>
                </div>
              )}

              {/* CTA */}
              <button
                onClick={() => setSelectedCourse(null)}
                className="mt-2 w-full rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 py-3 font-semibold text-white shadow-md transition hover:scale-[1.02] hover:from-purple-700 hover:to-indigo-700"
              >
                {i18n.language === "ar" ? "حسنًا" : "Got it"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trainer Registration Modal */}
      {showTrainerModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <div
            className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
            dir="rtl"
          >
            <div className="h-2 w-full rounded-t-2xl bg-[#1dacff]" />
            <button
              onClick={() => setShowTrainerModal(false)}
              className="absolute left-4 top-4 rounded-full p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700"
            >
              <HiX size={20} />
            </button>

            <div className="p-6 pt-10 text-right">
              <h2 className="mb-1 text-xl font-bold text-[#1dacff]">شروط التقديم لبرنامج المدربين</h2>
              <p className="mb-5 text-sm text-gray-500">يرجى قراءة الشروط والمتطلبات بعناية قبل التقديم.</p>

              <div className="space-y-5 text-sm text-gray-700">
                <div className="rounded-xl bg-[#1dacff]/10 px-4 py-3">
                  <p className="font-semibold text-[#1dacff]">العمر بين 20–35 سنة</p>
                </div>

                <section className="space-y-2">
                  <h3 className="mb-3 font-semibold text-gray-800">أن يكون المتقدم:</h3>
                  {[
                    "من موظفي مراكز الشباب أو مدربي محطات المستقبل، ذو خبرة تقنية أو مهتم بالتقنيات الحديثة",
                    "أن يمتلك خلفية في تكنولوجيا المعلومات، وأن يكون قد أتم مسار 'أساسيات الذكاء الاصطناعي' في JoCodes",
                    "أن يلتزم بحضور برنامج التدريب الكامل (5 أيام)",
                    "التفرغ والقدرة على تنفيذ أنشطة البرنامج (جلسات توعية، جلسات دعم، جلسات تفاعلية) داخل محافظته",
                    "عدد ساعات الالتزام / الشهر: 10 ساعات",
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[#1dacff]" />
                      <p className="text-gray-600">{item}</p>
                    </div>
                  ))}
                </section>

                <div className="border-t border-gray-100" />

                <section className="space-y-3">
                  <h3 className="font-semibold text-gray-800">مدة التحضير والجدول الزمني:</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-lg bg-gray-50 px-3 py-2">
                      <p className="text-xs text-gray-500">التسجيل</p>
                      <p className="font-medium text-gray-800">1/6 – 14/6</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 px-3 py-2">
                      <p className="text-xs text-gray-500">اختيار المدربين</p>
                      <p className="font-medium text-gray-800">15/6 – 29/6</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 px-3 py-2">
                      <p className="text-xs text-gray-500">بداية التدريب</p>
                      <p className="font-medium text-gray-800">30/6</p>
                    </div>
                    <div className="rounded-lg bg-gray-50 px-3 py-2">
                      <p className="text-xs text-gray-500">مدة الالتزام</p>
                      <p className="font-medium text-gray-800">حتى 31/12/2026</p>
                    </div>
                  </div>
                </section>
              </div>

              <div className="mt-6 flex justify-start gap-2">
                <button
                  onClick={() => setShowTrainerModal(false)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50"
                >
                  إغلاق
                </button>
                <button
                  onClick={() => {
                    setShowTrainerModal(false);
                    navigate("/trainer-register");
                  }}
                  className="rounded-lg bg-[#1dacff] px-4 py-2 text-sm font-medium text-white transition hover:bg-[#1dacff]/90"
                >
                  تقدّم الآن
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
